import React, { useEffect } from "react";
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import { Home, GraduationCap, FolderOpen, Mic, MapPin, MessageCircle, Scale, LogIn, LogOut, UserCircle2, Languages, UserRound, FileText, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";

const NAV = [
  { to: "/", key: "nav.home", icon: Home, testid: "nav-home" },
  { to: "/learn", key: "nav.learn", icon: GraduationCap, testid: "nav-learn" },
  { to: "/my-case", key: "nav.my_case", icon: FolderOpen, testid: "nav-my-case" },
  { to: "/practice", key: "nav.practice", icon: Mic, testid: "nav-practice" },
  { to: "/resources", key: "nav.resources", icon: MapPin, testid: "nav-resources" },
];

export default function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t, toggle, lang } = useLang();

  // Nudge free users when they hit 8/10 or 9/10 uploads (once per session per threshold)
  useEffect(() => {
    if (!user || user.tier !== "free") return;
    const limit = user.limits?.uploads ?? 10;
    if (limit < 0) return;
    const used = Number((user.usage || {}).summarize || 0);
    const remaining = limit - used;
    if (remaining > 2 || remaining < 1) return; // fire at 2 or 1 uploads left
    const key = `pod_nudge_free_${used}_${user.id}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    const msg = lang === "es"
      ? `Te quedan ${remaining} de ${limit} cargas gratis — Basic desbloquea ${24 - limit + remaining + used} por $19.99/año`
      : `You have ${remaining} of ${limit} free uploads left — Basic unlocks 24 for $19.99/year`;
    toast(msg, {
      duration: 8000,
      action: {
        label: lang === "es" ? "Ver planes" : "See plans",
        onClick: () => navigate("/pricing"),
      },
    });
  }, [user, lang, navigate]);

  return (
    <div className="min-h-screen bg-[#FBF9F5] text-[#1C2024]">
      <header className="border-b-2 border-[#E4DDD3] bg-[#FBF9F5]/90 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 py-2.5 sm:py-4 flex items-center justify-between gap-2 sm:gap-3">
          <NavLink to="/" className="flex items-center gap-2 sm:gap-3 pod-focus min-w-0" data-testid="brand-link">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-[#C85A32] text-white flex items-center justify-center shadow-sm shrink-0">
              <Scale className="w-4 h-4 sm:w-5 sm:h-5" />
            </div>
            <div className="leading-tight min-w-0">
              <div className="hidden sm:block text-xs uppercase tracking-widest text-[#707880] font-semibold">{t("brand.eyebrow")}</div>
              <div className="text-base sm:text-lg font-extrabold text-[#1C2024] -mt-0.5 whitespace-nowrap">{t("brand.title")}</div>
            </div>
          </NavLink>
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <button
              onClick={toggle}
              data-testid="lang-toggle"
              aria-label="Change language"
              title={lang === "en" ? "Español" : "English"}
              className="pod-focus text-xs font-bold border-2 border-[#E4DDD3] hover:border-[#C85A32] text-[#1C2024] px-2 sm:px-2.5 py-2 rounded-xl inline-flex items-center gap-1 sm:gap-1.5"
            >
              <Languages className="w-4 h-4" /> <span className="hidden xs:inline">{lang === "en" ? "ES" : "EN"}</span>
            </button>
            {user && typeof user === "object" ? (
              <>
                <div className="hidden sm:flex items-center gap-2 text-sm text-[#4A525A]" data-testid="header-user">
                  <UserCircle2 className="w-4 h-4 text-[#C85A32]" />
                  <span className="font-semibold">{user.name || user.email}</span>
                  {user.tier && user.tier !== "free" && (() => {
                    const labels = { basic: "Basic", complete: "Complete", court_ready_plus: "Court Ready+" };
                    const label = labels[user.tier] || "Premium";
                    return (
                      <span
                        className={`text-[10px] font-extrabold uppercase tracking-widest text-white px-2 py-0.5 rounded-full ${
                          user.tier === "court_ready_plus" ? "bg-[#C85A32]" : "bg-[#2C4A3E]"
                        }`}
                        data-testid="tier-badge"
                        title={
                          user.premium_expires_at
                            ? `${label} · ${lang === "es" ? "hasta" : "until"} ${new Date(user.premium_expires_at).toLocaleDateString(lang === "es" ? "es-US" : "en-US", { year: "numeric", month: "short", day: "numeric" })}`
                            : label
                        }
                      >
                        {label}
                      </span>
                    );
                  })()}
                </div>
                {(() => {
                  const limit = user.limits?.uploads ?? user.free_limit ?? 10;
                  if (limit < 0) return null; // Unlimited (Court Ready+) — hide pill
                  const usage = user.usage || {};
                  const remaining = Math.max(0, limit - (usage.summarize || 0));
                  return (
                    <NavLink
                      to="/pricing"
                      data-testid="header-quota-pill"
                      title={lang === "es" ? "Cargas de documentos restantes en tu plan" : "Document uploads remaining on your plan"}
                      className={`inline-flex text-[10px] sm:text-[11px] font-extrabold uppercase tracking-widest px-2 sm:px-2.5 py-1 rounded-full items-center gap-1 border-2 ${
                        remaining === 0
                          ? "bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100"
                          : "bg-[#FAF5EC] text-[#C85A32] border-[#E4DDD3] hover:border-[#C85A32]"
                      }`}
                    >
                      <Sparkles className="w-3 h-3" /> {remaining}/{limit}
                    </NavLink>
                  );
                })()}
                <button
                  onClick={logout}
                  data-testid="header-logout"
                  aria-label={t("auth.sign_out")}
                  title={t("auth.sign_out")}
                  className="pod-focus text-sm font-bold border-2 border-[#E4DDD3] hover:border-[#C85A32] text-[#1C2024] px-2.5 sm:px-3 py-2 rounded-xl inline-flex items-center gap-1.5 sm:gap-2"
                >
                  <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">{t("auth.sign_out")}</span>
                </button>
              </>
            ) : user === false ? (
              <>
                <Link
                  to="/login"
                  data-testid="header-login"
                  aria-label={t("auth.sign_in")}
                  className="pod-focus text-sm font-bold border-2 border-[#E4DDD3] hover:border-[#C85A32] text-[#1C2024] px-2.5 sm:px-3 py-2 rounded-xl inline-flex items-center gap-1.5 sm:gap-2"
                >
                  <LogIn className="w-4 h-4" /> <span className="hidden sm:inline">{t("auth.sign_in")}</span>
                </Link>
                <Link
                  to="/register"
                  data-testid="header-register"
                  className="pod-focus text-xs sm:text-sm font-bold bg-[#C85A32] hover:bg-[#B24C28] text-white px-2.5 sm:px-3 py-2 rounded-xl inline-flex items-center gap-1.5 sm:gap-2 whitespace-nowrap"
                >
                  <span className="sm:hidden">{lang === "es" ? "Gratis" : "Free"}</span>
                  <span className="hidden sm:inline">{t("auth.create_account")}</span>
                </Link>
              </>
            ) : (
              <span className="text-xs text-[#707880]" data-testid="header-auth-loading">{t("auth.loading")}</span>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 pb-32">{children}</main>

      <footer className="max-w-6xl mx-auto px-4 sm:px-6 pb-28 -mt-4" data-testid="layout-footer">
        <div className="border-t border-[#E4DDD3] pt-4 flex flex-wrap items-center justify-between gap-3 text-xs text-[#707880]">
          <div className="flex items-center gap-4">
            <NavLink
              to="/navigator"
              data-testid="footer-navigator"
              className="pod-focus inline-flex items-center gap-1.5 hover:text-[#C85A32] font-semibold"
            >
              <UserRound className="w-3.5 h-3.5" /> {lang === "es" ? "Navegadora" : "Navigator"}
            </NavLink>
            <NavLink
              to="/legal"
              data-testid="footer-legal"
              className="pod-focus inline-flex items-center gap-1.5 hover:text-[#C85A32] font-semibold"
            >
              <FileText className="w-3.5 h-3.5" /> {lang === "es" ? "Términos y Privacidad" : "Terms & Privacy"}
            </NavLink>
            <NavLink
              to="/pricing"
              data-testid="footer-pricing"
              className="pod-focus inline-flex items-center gap-1.5 hover:text-[#C85A32] font-semibold"
            >
              <Sparkles className="w-3.5 h-3.5" /> {lang === "es" ? "Precios" : "Pricing"}
            </NavLink>
          </div>
          <div className="text-[11px]">
            {lang === "es"
              ? "DIYLegal.info no brinda asesoría legal."
              : "DIYLegal.info does not provide legal advice."}
          </div>
        </div>
      </footer>
      <nav
        className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t-2 border-[#E4DDD3] z-50 shadow-2xl"
        data-testid="bottom-nav"
      >
        <div className="max-w-6xl mx-auto grid grid-cols-5 gap-1 px-1 py-2">
          {NAV.map((n) => {
            const Icon = n.icon;
            const active = location.pathname === n.to;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                className={`flex flex-col items-center gap-1 rounded-xl px-2 py-2 text-xs pod-focus transition-colors ${
                  active
                    ? "text-[#C85A32] bg-[#FAF5EC] font-bold"
                    : "text-[#4A525A] hover:text-[#1C2024] hover:bg-[#F3EFE6]"
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="leading-none text-[11px] sm:text-xs">{t(n.key)}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>
      {/* Floating Assistant bubble — shown on every page except the Assistant page itself */}
      {location.pathname !== "/deborah" && (
        <NavLink
          to="/deborah"
          data-testid="floating-assistant"
          aria-label={t("nav.deborah")}
          title={t("nav.deborah")}
          className={`pod-focus fixed right-4 z-40 w-14 h-14 rounded-full bg-[#C85A32] hover:bg-[#B24C28] text-white shadow-2xl flex items-center justify-center transition-transform hover:scale-105 active:scale-95 ${
            location.pathname === "/" ? "bottom-40 sm:bottom-24" : "bottom-24"
          }`}
        >
          <MessageCircle className="w-6 h-6" />
          <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-emerald-400 border-2 border-white" aria-hidden="true" />
        </NavLink>
      )}
    </div>
  );
}
